import React from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { LayoutDashboard, Coins, TrendingUp, Users, LogOut, Wallet, Settings, Store } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n";
import { Logo } from "@/components/Logo";
import { LanguageToggle } from "@/components/LanguageToggle";

const navItem = (active) =>
  `flex items-center gap-3 px-4 py-3 rounded-md text-sm font-medium transition-all duration-200 ${
    active ? "bg-[#00E5FF]/10 text-[#00E5FF] border-s-2 border-[#00E5FF]" : "text-zinc-400 hover:text-white hover:bg-white/5 border-s-2 border-transparent"
  }`;

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const handleLogout = () => { logout(); navigate("/login"); };

  const links = [
    { to: "/admin", label: t("nav.dashboard"), icon: LayoutDashboard, testid: "nav-admin-dashboard" },
    { to: "/admin/currencies", label: t("nav.currencies"), icon: Coins, testid: "nav-admin-currencies" },
    { to: "/admin/rates", label: t("nav.rates"), icon: TrendingUp, testid: "nav-admin-rates" },
    { to: "/admin/gateway", label: t("nav.gateway"), icon: Settings, testid: "nav-admin-gateway" },
    { to: "/admin/merchants", label: t("nav.merchants"), icon: Store, testid: "nav-admin-merchants" },
    { to: "/admin/users", label: t("nav.users"), icon: Users, testid: "nav-admin-users" },
  ];

  return (
    <div className="min-h-screen flex bg-black">
      <aside className="w-64 shrink-0 border-e border-zinc-900 bg-[#060606] flex flex-col fixed inset-y-0 start-0 z-20">
        <div className="px-6 py-6 border-b border-zinc-900 flex items-center justify-between">
          <Logo size="md" />
          <span className="text-[10px] font-semibold text-[#00E5FF] bg-[#00E5FF]/10 px-2 py-0.5 rounded-full">{t("app.admin")}</span>
        </div>
        <nav className="flex-1 px-3 py-5 space-y-1 overflow-y-auto">
          {links.map((l) => (
            <NavLink key={l.to} to={l.to} end={l.to === "/admin"} data-testid={l.testid} className={({ isActive }) => navItem(isActive)}>
              <l.icon size={18} strokeWidth={1.5} />
              <span>{l.label}</span>
            </NavLink>
          ))}
          <div className="pt-4 mt-4 border-t border-zinc-900">
            <NavLink to="/" data-testid="goto-client-panel" className={() => navItem(false)}>
              <Wallet size={18} strokeWidth={1.5} />
              <span>{t("nav.clientView")}</span>
            </NavLink>
          </div>
        </nav>
        <div className="px-3 pt-4 pb-20 border-t border-zinc-900">
          <div className="px-1 pb-3"><LanguageToggle className="w-full justify-center" /></div>
          <div className="px-4 py-2 mb-2">
            <div className="text-sm text-white font-medium truncate">{user?.name}</div>
            <div className="text-xs text-zinc-500 truncate" dir="ltr">{user?.email}</div>
          </div>
          <button onClick={handleLogout} data-testid="admin-logout-button"
            className="w-full flex items-center gap-3 px-4 py-2.5 rounded-md text-sm text-zinc-400 hover:text-red-400 hover:bg-red-500/5 transition-all">
            <LogOut size={18} strokeWidth={1.5} /> {t("nav.logout")}
          </button>
        </div>
      </aside>

      <main className="flex-1 ms-64 min-h-screen">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
